import { Container, Grid2, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { useSessionStorage } from 'react-use';

import BigCircle from '@components/BigCircle';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';

import { useIsMobile } from '@hooks/useIsMobile';
import { useShareId } from '@hooks/useShareId';

import logo from '@images/logos/vitall-white-red-full-logo.svg';

const messages = defineMessages({
  verifyEmail: {
    id: 'sharedResource.verifyEmail.title',
    defaultMessage: 'Access to a medical record has been shared with you',
  },
  accessResource: {
    id: 'sharedResource.access.title',
    defaultMessage: '{name} shared the following medical record with you',
  },
});

export enum SharedResourceStep {
  VerifyEmail = 'verifyEmail',
  Access = 'access',
}

/**
 * Props passed to the SharedResourceLayout component.
 */
interface SharedResourceLayoutChildrenProps {
  /**
   * The share id associated with the shared resource.
   */
  share_id: string;

  /**
   * The current step in the shared resource flow.
   */
  step?: SharedResourceStep;

  /**
   * Callback to call when the user verifies their email address.
   * @param token The share token associated with the shared resource.
   */
  handleVerified: (token: string) => void;

  /**
   * Callback to update the sharing user's name.
   * @param name The sharinguser's name.
   */
  setUserName: (name: string) => void;

  /**
   * Callback to reset the shared resource state.
   */
  reset: () => void;
}

interface SharedResourceLayoutProps {
  children?: (props: SharedResourceLayoutChildrenProps) => React.ReactNode;
}

function SharedResourceLayout({ children }: SharedResourceLayoutProps) {
  const { formatMessage } = useIntl();
  const [step, setStep] = useState<SharedResourceStep>();
  const [userName, setUserName] = useState<string>();
  const [token, setToken] = useSessionStorage('share_token', '');

  const shareId = useShareId();

  const isMobile = useIsMobile('md');

  useEffect(() => {
    // If share token found in local storage, show access screen. If not, show email screen
    if (token) {
      setStep(SharedResourceStep.Access);
    } else {
      setStep(SharedResourceStep.VerifyEmail);
    }
  }, [token]);

  const handleVerified = (token: string) => {
    setToken(token);
  };

  const reset = () => {
    setUserName(undefined);
    // Resetting share token takes user back to verify email step
    setToken('');
  };

  // Title changes based on step
  let title: React.ReactNode;
  switch (step) {
    case SharedResourceStep.VerifyEmail:
      title = formatMessage(messages.verifyEmail);
      break;
    case SharedResourceStep.Access:
      title = userName
        ? formatMessage(messages.accessResource, {
            name: userName,
          })
        : null;
      break;
    default:
      title = null;
      break;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 8, px: 3 }}>
      <BigCircle show />
      <Stack spacing={2}>
        <Grid2
          container
          justifyContent="space-between"
          alignItems="baseline"
          flexWrap="wrap-reverse"
          rowGap={3}
        >
          <Grid2 size={{ md: 8, xs: isMobile ? 12 : undefined }}>
            <Typography
              color="primary.contrastText"
              variant={isMobile ? 'h5' : 'h3'}
            >
              {title}
            </Typography>
          </Grid2>
          <Grid2
            sx={{
              textAlign: {
                xs: 'center',
                md: 'end',
              },
            }}
            size={{ xs: isMobile ? 12 : undefined }}
          >
            <img src={logo} alt="Vitall Logo" />
          </Grid2>
        </Grid2>
        <Container maxWidth="lg" disableGutters sx={{ mt: 0 }}>
          {typeof shareId === 'string' && children
            ? children({
                share_id: shareId,
                handleVerified,
                step,
                setUserName,
                reset,
              })
            : null}
        </Container>
      </Stack>
    </Container>
  );
}

export default withErrorBoundary(SharedResourceLayout, {
  FallbackComponent: ErrorBoundaryFallback,
});
